import { useQuery } from "@tanstack/react-query";

import { apiDownload, type Schemas } from "../../lib/api/client";

/** A blob as a data: URL, which the page's CSP allows for images only. */
function dataUrlOf(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("The logo could not be read."));
    reader.readAsDataURL(blob);
  });
}

/**
 * The business's logo as an image source: undefined while it loads, null
 * when there is none or it could not be read. It is fetched with the session (the API's resource policy keeps it
 * same-origin, so an <img> cannot load it across origins), once per logo:
 * a logo never changes under its ID. Signing out removes it with the rest.
 */
export function useLogoSource(logo: Schemas["BrandingLogo"] | null | undefined): string | null | undefined {
  const query = useQuery({
    enabled: Boolean(logo),
    queryFn: async () => dataUrlOf((await apiDownload(`/v1/branding/logos/${logo?.logo_id}`, "image/png, image/jpeg")).blob),
    queryKey: ["branding-logo", logo?.logo_id],
    staleTime: Number.POSITIVE_INFINITY,
  });
  if (!logo || query.isError) return null;
  return query.data;
}
